import React from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import customerLogo from '../../../assets/customerLogo.png';
import { CurrencySymbol, NamedLink } from '../../../components';
import useCategoryConfigMap from '../../../hooks/useCategoryConfigMap';
import { formatListingPriceAmount } from '../../../util/currency';
import { configForListing } from '../../../util/data';
import { createSlug } from '../../../util/urlHelpers';
import { metersToText } from './mapListings';

import css from './MapListingCard.module.css';

const directionsUrl = ({ lat, lng }) =>
  `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

/**
 * Preview of the listing under the selected pin. A pin with several listings
 * pages through them (prev / next, "1 of N"). No countdown timers: the list
 * deal card ticks every second, which is wasteful on a map.
 */
const MapListingCard = props => {
  const { group, index, onIndexChange, onClose, intl, className } = props;
  const categoryConfigMap = useCategoryConfigMap();
  const total = group.listings.length;
  const listing = group.listings[Math.min(index, total - 1)];
  const {
    id,
    kind,
    title,
    image,
    merchantName,
    address,
    averageRating,
    totalReviews,
    priceAmount,
    publicData,
    distanceMeters,
    coordinate,
  } = listing;

  const model = configForListing(categoryConfigMap, publicData)?.transactionModel;
  const isDeal = kind === 'deal';
  const isNegotiated = !isDeal && model === 'negotiated';
  const hasPrice = priceAmount !== undefined && !isNegotiated;
  const meta = [merchantName, distanceMeters !== undefined ? metersToText(distanceMeters) : '']
    .filter(Boolean)
    .join(' · ');

  return (
    <div className={classNames(css.root, className)}>
      <button type="button" className={css.close} onClick={onClose} aria-label="close">
        ×
      </button>
      {total > 1 ? (
        <div className={css.pager}>
          <button
            type="button"
            className={css.pagerButton}
            disabled={index <= 0}
            onClick={() => onIndexChange(index - 1)}
            aria-label="previous"
          >
            ‹
          </button>
          <span className={css.pagerText}>
            <FormattedMessage
              id="AlgoliaMapView.pagerOf"
              values={{ index: index + 1, total }}
            />
          </span>
          <button
            type="button"
            className={css.pagerButton}
            disabled={index >= total - 1}
            onClick={() => onIndexChange(index + 1)}
            aria-label="next"
          >
            ›
          </button>
        </div>
      ) : null}

      <div className={css.body}>
        <img className={css.image} src={image || customerLogo} alt={title} />
        <div className={css.info}>
          <div className={css.title}>{title}</div>
          {meta ? <div className={css.meta}>{meta}</div> : null}
          <div className={css.rating}>
            {'★'.repeat(Math.round(averageRating))}
            {'☆'.repeat(5 - Math.round(averageRating))} {averageRating.toFixed(1)} ({totalReviews})
          </div>
          {address ? <div className={css.address}>{address}</div> : null}
          <div className={css.price}>
            {hasPrice ? (
              <>
                <CurrencySymbol size={14} /> {formatListingPriceAmount(intl, priceAmount / 100)}
                {model === 'hourly' ? (
                  <span className={css.unit}>
                    {' '}
                    <FormattedMessage id="ListingCard.perHourSuffix" />
                  </span>
                ) : null}
              </>
            ) : isNegotiated ? null : (
              <FormattedMessage id="AlgoliaMapView.priceUnavailable" />
            )}
          </div>
        </div>
      </div>

      <div className={css.actions}>
        <a
          className={css.secondary}
          href={directionsUrl(coordinate)}
          target="_blank"
          rel="noopener noreferrer"
        >
          <FormattedMessage id="AlgoliaMapView.directions" />
        </a>
        <NamedLink
          className={css.primary}
          name="ListingPage"
          params={{ id, slug: createSlug(title) }}
        >
          <FormattedMessage
            id={
              isDeal
                ? 'AlgoliaMapView.viewDeal'
                : isNegotiated
                ? 'SearchPage.getQuote'
                : 'SearchPage.book'
            }
          />
        </NamedLink>
      </div>
    </div>
  );
};

export default MapListingCard;
